import { genKey } from "draft-js";

const tableStructure = {
  makeCell: (params) => {
    return {
      key: genKey(),
      ...params,
    };
  },

  cellDefaults: () => {
    return {
      borderTop: false,
      borderRight: false,
      borderLeft: false,
      borderBottom: false,
      padding: 2,
      backgroundColor: "#FFFFFF",
      verticalAlign: "top",
    };
  },

  applyBorderPreset: (table, selectedKeys, preset) => {
    if (!Array.isArray(table) || !Array.isArray(selectedKeys) || !selectedKeys.length) {
      return table;
    }
    const selectedLocations = selectedKeys
      .map((key) => tableStructure.cellLocate(table, key))
      .filter((location) => Number.isInteger(location.row) && Number.isInteger(location.col));
    if (!selectedLocations.length) return table;

    const selected = new Set(
      selectedLocations.map((location) => `${location.row}:${location.col}`)
    );
    const hasSelectedCell = (row, col) => selected.has(`${row}:${col}`);
    const clearBorders = (cell) => ({
      ...cell,
      borderLeft: false,
      borderRight: false,
      borderTop: false,
      borderBottom: false,
    });
    const result = table.map((row) => row.map((cell) => ({ ...cell })));

    selectedLocations.forEach(({ row, col }) => {
      const cell = clearBorders(result[row][col]);
      if (preset === "all") {
        result[row][col] = {
          ...cell,
          borderLeft: true,
          borderRight: true,
          borderTop: true,
          borderBottom: true,
        };
        return;
      }
      if (preset === "none") {
        result[row][col] = cell;
        return;
      }
      if (preset === "outer") {
        result[row][col] = {
          ...cell,
          borderLeft: !hasSelectedCell(row, col - 1),
          borderRight: !hasSelectedCell(row, col + 1),
          borderTop: !hasSelectedCell(row - 1, col),
          borderBottom: !hasSelectedCell(row + 1, col),
        };
        return;
      }
      if (preset === "inner") {
        result[row][col] = {
          ...cell,
          borderLeft: hasSelectedCell(row, col - 1),
          borderRight: hasSelectedCell(row, col + 1),
          borderTop: hasSelectedCell(row - 1, col),
          borderBottom: hasSelectedCell(row + 1, col),
        };
        return;
      }
      if (preset === "inner-horizontal") {
        result[row][col] = {
          ...cell,
          borderTop: hasSelectedCell(row - 1, col),
          borderBottom: hasSelectedCell(row + 1, col),
        };
        return;
      }
      if (preset === "inner-vertical") {
        result[row][col] = {
          ...cell,
          borderLeft: hasSelectedCell(row, col - 1),
          borderRight: hasSelectedCell(row, col + 1),
        };
      }
    });

    // A shared edge can be represented by either of the adjacent cells.
    // Keep the outside neighbour in sync so that a hidden border cannot be
    // left visible merely because it was stored on the other cell.
    [
      [-1, 0, "borderTop", "borderBottom"],
      [1, 0, "borderBottom", "borderTop"],
      [0, -1, "borderLeft", "borderRight"],
      [0, 1, "borderRight", "borderLeft"],
    ].forEach(([rowOffset, colOffset, side, oppositeSide]) => {
      selectedLocations.forEach(({ row, col }) => {
        const neighbourRow = row + rowOffset;
        const neighbourCol = col + colOffset;
        if (
          hasSelectedCell(neighbourRow, neighbourCol) ||
          !result[neighbourRow] ||
          !result[neighbourRow][neighbourCol]
        ) {
          return;
        }
        result[neighbourRow][neighbourCol] = {
          ...result[neighbourRow][neighbourCol],
          [oppositeSide]: result[row][col][side],
        };
      });
    });
    return tableStructure.normaliseRoot(result);
  },

  create: (rows, cols) => {
    let table = [];
    const cellDefaults = tableStructure.cellDefaults();
    const defaultWidth = 100 / cols;
    for (let row = 0; row < rows; row++) {
      let line = [];
      for (let col = 0; col < cols; col++) {
        line.push(
          tableStructure.makeCell({
            width: defaultWidth,
            text: ``,
            self: col === 0 && row === 0,
            ...cellDefaults,
            borderTop: true,
            borderRight: true,
            borderBottom: true,
            borderLeft: true,
          })
        );
      }
      table.push(line);
    }
    return table;
  },

  normaliseRoot: (table) => {
    if (!Array.isArray(table) || !table.length || !table[0].length) return [];
    return table.map((row, rowIndex) =>
      row.map((cell, colIndex) => ({
        ...cell,
        self: rowIndex === 0 && colIndex === 0,
      }))
    );
  },

  emptyCellLike: (cell) => {
    const result = { ...cell, key: genKey(), text: "", self: false };
    delete result.table;
    delete result.rootKey;
    return result;
  },

  colAdd: (table, currentCol, direction) => {
    if (!Array.isArray(table) || !table.length || !table[0].length) return [];
    const insertAt = direction === "left" ? currentCol : currentCol + 1;
    const resultTable = table.map((row) => {
      const reference = row[Math.min(currentCol, row.length - 1)];
      const line = row.map((cell) => ({ ...cell }));
      line.splice(insertAt, 0, tableStructure.emptyCellLike(reference));
      return line;
    });
    return tableStructure.normaliseRoot(resultTable);
  },

  rowAdd: (table, currentRow, direction) => {
    if (!Array.isArray(table) || !table.length || !table[0].length) return [];
    const insertAt = direction === "up" ? currentRow : currentRow + 1;
    const resultTable = table.map((row) => row.map((cell) => ({ ...cell })));
    resultTable.splice(
      insertAt,
      0,
      table[Math.min(currentRow, table.length - 1)].map((cell) =>
        tableStructure.emptyCellLike(cell)
      )
    );
    return tableStructure.normaliseRoot(resultTable);
  },

  duplicateRow: (row, rowIndex, insertNewCells = false, rootCell = false) => {
    let resultRow = [];
    if (row && Array.isArray(row)) {
      row.forEach((cell, colIdx) => {
        resultRow.push({
          ...cell,
          key: insertNewCells ? genKey() : cell.key,
          text: insertNewCells ? "" : cell.text,
          self: rootCell && colIdx === 0,
        });
      });
    }
    return resultRow;
  },

  rowRemove: (table, rowIndex) => {
    if (!Array.isArray(table) || table.length <= 1) return [];
    return tableStructure.normaliseRoot(
      table
        .filter((row, index) => index !== rowIndex)
        .map((row) => row.map((cell) => ({ ...cell })))
    );
  },

  colRemove: (table, colIndex) => {
    if (!Array.isArray(table) || !table.length || table[0].length <= 1) {
      return [];
    }
    return tableStructure.normaliseRoot(
      table.map((row) =>
        row
          .filter((cell, index) => index !== colIndex)
          .map((cell) => ({ ...cell }))
      )
    );
  },

  // Column widths belong to the table grid, rather than to an individual
  // rendered cell. Keeping a canonical list lets a resize work with merged
  // cells as well: their displayed width is the sum of their grid columns.
  getColumnWidths: (table) => {
    if (!Array.isArray(table) || !table.length || !table[0].length) return [];
    return table[0].map((cell, colIndex) => {
      let master = cell;
      let masterCol = colIndex;
      if (cell.hidden && cell.mergedInto) {
        const location = tableStructure.cellLocate(table, cell.mergedInto);
        if (Number.isInteger(location.row) && Number.isInteger(location.col)) {
          master = table[location.row][location.col];
          masterCol = location.col;
        }
      }
      const offset = colIndex - masterCol;
      if (master.colSpan > 1) {
        const mergedWidth = master.mergedWidths && master.mergedWidths[offset];
        if (Number(mergedWidth) > 0) return Number(mergedWidth);
        return (Number(master.width) || 0) / master.colSpan;
      }
      return Number(cell.width) || 100 / table[0].length;
    });
  },

  applyColumnWidths: (table, widths) => {
    if (!Array.isArray(table) || !Array.isArray(widths)) return table;
    return tableStructure.normaliseRoot(
      table.map((row) =>
        row.map((cell, colIndex) => {
          const colSpan = cell.colSpan || 1;
          const mergedWidths = widths.slice(colIndex, colIndex + colSpan);
          return {
            ...cell,
            width: mergedWidths.reduce((sum, width) => sum + (Number(width) || 0), 0),
            ...(colSpan > 1 ? { mergedWidths } : {}),
          };
        })
      )
    );
  },

  getRowHeights: (table) =>
    Array.isArray(table)
      ? table.map((row) => (row[0] && Number(row[0].rowHeight)) || null)
      : [],

  applyRowHeights: (table, heights) => {
    if (!Array.isArray(table) || !Array.isArray(heights)) return table;
    return tableStructure.normaliseRoot(
      table.map((row, rowIndex) =>
        row.map((cell) => ({ ...cell, rowHeight: heights[rowIndex] || null }))
      )
    );
  },

  mergeCells: (table, selectedKeys) => {
    if (!Array.isArray(table) || !Array.isArray(selectedKeys) || selectedKeys.length < 2) {
      return false;
    }
    const selected = new Set(selectedKeys);
    const locations = [];
    table.forEach((row, rowIndex) =>
      row.forEach((cell, colIndex) => {
        if (selected.has(cell.key)) locations.push({ row: rowIndex, col: colIndex });
      })
    );
    if (locations.length !== selected.size) return false;

    const minRow = Math.min(...locations.map((location) => location.row));
    const maxRow = Math.max(...locations.map((location) => location.row));
    const minCol = Math.min(...locations.map((location) => location.col));
    const maxCol = Math.max(...locations.map((location) => location.col));
    const selectedCount = (maxRow - minRow + 1) * (maxCol - minCol + 1);
    if (selectedCount !== selected.size) return false;

    for (let row = minRow; row <= maxRow; row++) {
      for (let col = minCol; col <= maxCol; col++) {
        const cell = table[row][col];
        if (
          !selected.has(cell.key) ||
          cell.hidden ||
          (cell.rowSpan && cell.rowSpan > 1) ||
          (cell.colSpan && cell.colSpan > 1)
        ) {
          return false;
        }
      }
    }

    const master = table[minRow][minCol];
    const mergedWidths = table[minRow]
      .slice(minCol, maxCol + 1)
      .map((cell) => cell.width);
    const result = table.map((row, rowIndex) =>
      row.map((cell, colIndex) => {
        if (rowIndex < minRow || rowIndex > maxRow || colIndex < minCol || colIndex > maxCol) {
          return { ...cell };
        }
        if (rowIndex === minRow && colIndex === minCol) {
          return {
            ...cell,
            rowSpan: maxRow - minRow + 1,
            colSpan: maxCol - minCol + 1,
            width: mergedWidths.reduce((sum, width) => sum + (Number(width) || 0), 0),
            mergedWidths,
            hidden: false,
          };
        }
        return {
          ...cell,
          text: "",
          hidden: true,
          mergedInto: master.key,
        };
      })
    );

    // Text is collected row-by-row before it is assigned to the master cell.
    result[minRow][minCol].text = table
      .slice(minRow, maxRow + 1)
      .flatMap((row) => row.slice(minCol, maxCol + 1))
      .map((cell) => cell.text)
      .filter(Boolean)
      .join("\n");
    return { table: tableStructure.normaliseRoot(result), masterKey: master.key };
  },

  splitCell: (table, key) => {
    const location = tableStructure.cellLocate(table, key);
    if (!Number.isInteger(location.row) || !Number.isInteger(location.col)) return false;
    const clicked = table[location.row][location.col];
    const masterLocation = clicked.hidden
      ? tableStructure.cellLocate(table, clicked.mergedInto)
      : location;
    if (!Number.isInteger(masterLocation.row) || !Number.isInteger(masterLocation.col)) {
      return false;
    }
    const master = table[masterLocation.row][masterLocation.col];
    const rowSpan = master.rowSpan || 1;
    const colSpan = master.colSpan || 1;
    if (rowSpan === 1 && colSpan === 1) {
      const originalWidth = Number(master.width) || 100 / table[0].length;
      const halfWidth = originalWidth / 2;
      const result = table.map((row, rowIndex) => {
        const line = [];
        row.forEach((cell, colIndex) => {
          if (colIndex !== location.col) {
            line.push({ ...cell });
            return;
          }

          if (rowIndex === location.row) {
            line.push({ ...cell, width: halfWidth });
            line.push({
              ...tableStructure.emptyCellLike(cell),
              width: halfWidth,
              text: "",
            });
            return;
          }

          // The rest of the row keeps one visible cell over the same area.
          // A hidden grid item preserves a rectangular data model without
          // making a new visible column in those rows.
          line.push({
            ...cell,
            width: originalWidth,
            colSpan: (cell.colSpan || 1) + 1,
            mergedWidths: [halfWidth, halfWidth],
          });
          line.push({
            ...tableStructure.emptyCellLike(cell),
            width: halfWidth,
            hidden: true,
            mergedInto: cell.key,
          });
        });
        return line;
      });
      return {
        table: tableStructure.normaliseRoot(result),
        masterKey: master.key,
      };
    }

    const splitByColumns = colSpan > 1 && colSpan >= rowSpan;
    const firstSpan = Math.ceil((splitByColumns ? colSpan : rowSpan) / 2);
    const secondMasterLocation = splitByColumns
      ? { row: masterLocation.row, col: masterLocation.col + firstSpan }
      : { row: masterLocation.row + firstSpan, col: masterLocation.col };
    const secondMasterKey = table[secondMasterLocation.row][secondMasterLocation.col].key;
    const clearMerge = (cell, text = "") => {
      const {
        rowSpan: ignoredRowSpan,
        colSpan: ignoredColSpan,
        hidden,
        mergedInto,
        mergedWidths,
        ...plainCell
      } = cell;
      return { ...plainCell, text };
    };
    const areaCell = (rowIndex, colIndex, masterRow, masterCol, currentMasterKey, currentText, currentRowSpan, currentColSpan) => {
      const isMaster = rowIndex === masterRow && colIndex === masterCol;
      const baseCell = table[rowIndex][colIndex];
      if (isMaster) {
        const width = splitByColumns
          ? (master.mergedWidths || []).slice(
              masterCol - masterLocation.col,
              masterCol - masterLocation.col + currentColSpan
            ).reduce((sum, value) => sum + (Number(value) || 0), 0)
          : baseCell.width;
        return {
          ...clearMerge(baseCell, currentText),
          key: currentMasterKey,
          width: width || baseCell.width,
          rowSpan: currentRowSpan,
          colSpan: currentColSpan,
          mergedWidths: splitByColumns
            ? (master.mergedWidths || []).slice(
                masterCol - masterLocation.col,
                masterCol - masterLocation.col + currentColSpan
              )
            : undefined,
          hidden: false,
        };
      }
      return {
        ...clearMerge(baseCell),
        hidden: true,
        mergedInto: currentMasterKey,
      };
    };

    const result = table.map((row, rowIndex) =>
      row.map((cell, colIndex) => {
        const inMergedArea =
          rowIndex >= masterLocation.row &&
          rowIndex < masterLocation.row + rowSpan &&
          colIndex >= masterLocation.col &&
          colIndex < masterLocation.col + colSpan;
        if (!inMergedArea) return { ...cell };

        const inFirstPart = splitByColumns
          ? colIndex < secondMasterLocation.col
          : rowIndex < secondMasterLocation.row;
        if (inFirstPart) {
          return areaCell(
            rowIndex,
            colIndex,
            masterLocation.row,
            masterLocation.col,
            master.key,
            master.text,
            splitByColumns ? rowSpan : firstSpan,
            splitByColumns ? firstSpan : colSpan
          );
        }
        return areaCell(
          rowIndex,
          colIndex,
          secondMasterLocation.row,
          secondMasterLocation.col,
          secondMasterKey,
          "",
          splitByColumns ? rowSpan : rowSpan - firstSpan,
          splitByColumns ? colSpan - firstSpan : colSpan
        );
      })
    );
    return { table: tableStructure.normaliseRoot(result), masterKey: master.key };
  },

  cellLocate: (table, key) => {
    let result = {};
    if (table && Array.isArray(table)) {
      table.forEach((row, rowIdx) => {
        row.forEach((col, colIdx) => {
          if (key === col.key) {
            result = {
              row: rowIdx,
              col: colIdx,
            };
          }
        });
      });
    }
    return result;
  },

  getAllKeys: (table) => {
    let result = [];
    if (table && Array.isArray(table)) {
      table.forEach((row, rowIdx) => {
        row.forEach((col, colIdx) => {
          result.push(col.key);
        });
      });
    }
    return result;
  },

  getDiffKeys: (table1, table2) => {
    let keys1 = tableStructure.getAllKeys(table1);
    let keys2 = tableStructure.getAllKeys(table2);
    let diff = keys2.filter((key) => !keys1.includes(key));
    return diff;
  },
};
export default tableStructure;
